import * as React from "react";
import { Group } from "@vx/group";
import { GlyphDot } from "@vx/glyph";
import { LinePath, AreaClosed } from "@vx/shape";
import { scaleBand, scaleTime, scaleLinear, scaleLog } from "@vx/scale";
import { Grid } from "@vx/grid";
import { curveMonotoneX, curveLinear, curveBasis, curveStep } from "@vx/curve";
import { max, min } from "d3-array";
import { AxisLeft, AxisBottom } from "@vx/axis";
import { format } from "date-fns";
import { Text } from "@vx/text";
import GraphLegend from "./GraphLegend";
import TimeMarker from "./TimeMarker";
import { motion } from "framer-motion";
import { localPoint } from "@vx/event";
import { closestIndexTo } from "date-fns";

export type ChartData = {
  id: number;
  date: Date;
  name: string;
  total: number;
  delta: number;
  deaths: number;
  ic: number;
  infected: number;
};

type ScaleType = "linear" | "log";

export interface Props {
  width: number;
  height: number;
  data: ChartData[];
  activeIndex: number;
}
export const cTotal = "#f67280";
export const cIC = "#59BDEB";
export const cDelta = "#F6BD81";
export const cDeath = "#000000";
export const cInfected = "#48BB78";
const AREA_OPACITY = 0.1;
export default function FullGraph({ width, height, data, activeIndex }) {
  const [index, _index] = React.useState(data.length - 1);
  const [scaleType, _scaleType] = React.useState<ScaleType>("linear");

  React.useEffect(() => {
    setTimeout(() => {
      _index(activeIndex);
    }, 50);
  }, [activeIndex]);
  const dates = React.useMemo(() => {
    return data.map(d => d.date);
  }, [data]);

  const margin = {
    top: 20,
    left: width < 500 ? 40 : 50,
    bottom: 40,
    right: width < 500 ? 10 : 20
  };
  // bounds
  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  // accessors
  const date = (d: ChartData) => d.date;
  const total = (d: ChartData) => d.total;
  const delta = (d: ChartData) => d.delta;
  const deaths = (d: ChartData) => d.deaths;
  const ics = (d: ChartData) => d.ic;
  const infected = (d: ChartData) => d.infected;

  let maxV = max(data.map(infected));

  const yScale = React.useMemo(() => {
    switch (scaleType) {
      case "log":
        return scaleLog<number>({
          domain: [1, maxV],
          rangeRound: [yMax, 0],
          range: [yMax, 0],
          nice: true
        });
      default:
        return scaleLinear<number>({
          rangeRound: [yMax, 0],
          range: [yMax, 0],
          domain: [0, maxV],
          nice: true
        });
    }
  }, [scaleType]);
  const xScale = scaleTime<number>({
    domain: [Math.min(...data.map(date)), Math.max(...data.map(date))],
    clamp: true
  });

  // positions
  const x = (d: ChartData) => xScale(date(d)) as number;
  const y = (d: ChartData) => yScale(d.total !== 0 ? d.total : 1) as number;
  const y2 = (d: ChartData) => {
    if (d.delta === 0) return yScale(1);
    return yScale(delta(d));
  };
  const y3 = (d: ChartData) => yScale(d.deaths !== 0 ? d.deaths : 1) as number;
  const y4 = (d: ChartData) => yScale(d.ic !== 0 ? d.ic : 1) as number;
  const y5 = (d: ChartData) => yScale(d.infected !== 0 ? d.infected : 1) as number;

  // colors

  const contrast = "#ffffff";

  // update scale range to match bounds
  xScale.range([0, xMax]);
  yScale.range([yMax, 0]);

  const tickValues = [data[1].date, data[Math.round(data.length / 2)].date, data[data.length - 1].date];
  const handleTooltip = event => {
    const coords = localPoint(event.target.ownerSVGElement, event);

    const x0 = xScale.invert(coords.x - margin.left);
    let d = new Date(x0);
    let c = closestIndexTo(d, dates);

    if (c !== index) {
      _index(c);
    }
  };

  return (
    <div>
      <svg width={width} height={height}>
        <Grid
          top={margin.top}
          left={margin.left}
          xScale={xScale}
          yScale={yScale}
          stroke="rgba(0, 0, 0, 0.05)"
          width={xMax}
          height={yMax}
          numTicksRows={10}
          numTicksColumns={data.length}
        />

        <Group top={margin.top} left={margin.left}>
          <AreaClosed
            data={data}
            x={(d: ChartData) => x(d)}
            y={(d: ChartData) => y(d)}
            yScale={yScale}
            strokeWidth={1}
            fill={cTotal}
            curve={curveMonotoneX}
            opacity={AREA_OPACITY}
          />

          <AreaClosed
            data={data}
            x={(d: ChartData) => x(d)}
            y={(d: ChartData) => y2(d)}
            yScale={yScale}
            strokeWidth={1}
            fill={cDelta}
            curve={curveMonotoneX}
            opacity={AREA_OPACITY}
          />
          <AreaClosed
            data={data}
            x={(d: ChartData) => x(d)}
            y={(d: ChartData) => y3(d)}
            yScale={yScale}
            strokeWidth={1}
            fill={cDeath}
            curve={curveMonotoneX}
            opacity={AREA_OPACITY}
          />
          <AreaClosed
            data={data}
            x={(d: ChartData) => x(d)}
            y={(d: ChartData) => y4(d)}
            yScale={yScale}
            strokeWidth={1}
            fill={cIC}
            curve={curveMonotoneX}
            opacity={AREA_OPACITY}
          />
          <AreaClosed
            data={data}
            x={(d: ChartData) => x(d)}
            y={(d: ChartData) => y5(d)}
            yScale={yScale}
            strokeWidth={1}
            fill={cInfected}
            curve={curveMonotoneX}
            opacity={AREA_OPACITY}
          />

          <LinePath data={data} x={x} y={y} stroke={cTotal} strokeWidth={2} curve={curveMonotoneX} />
          <LinePath data={data} x={x} y={y2} stroke={cDelta} strokeWidth={2} curve={curveMonotoneX} />
          <LinePath data={data} x={x} y={y3} stroke={cDeath} strokeWidth={2} curve={curveMonotoneX} />
          <LinePath data={data} x={x} y={y4} stroke={cIC} strokeWidth={2} curve={curveMonotoneX} />
          <LinePath data={data} x={x} y={y5} stroke={cInfected} strokeWidth={2} curve={curveMonotoneX} />

          <rect
            opacity={0}
            width={width - margin.left - margin.right}
            height={height - margin.top - margin.bottom}
            onTouchStart={handleTooltip}
            onTouchMove={handleTooltip}
            onMouseMove={handleTooltip}
          />
          <TimeMarker
            width={width - margin.left - margin.right}
            height={height - margin.bottom - margin.top}
            datum={data[index]}
            x={x}
            y1={y}
            y2={y2}
            y3={y3}
          />
        </Group>

        <Group key={"axi"} top={margin.top} left={margin.left}>
          <AxisLeft
            scale={yScale}
            tickFormat={d => {
              return d + "";
            }}
            numTicks={4}
            hideTicks={true}
            tickLabelProps={() => ({
              fill: "#333",
              fontSize: 8,
              textAnchor: "end",
              verticalAnchor: "middle"
            })}
          />
          <AxisBottom
            scale={xScale}
            top={yMax}
            stroke={"#333"}
            tickStroke={"#333"}
            tickValues={tickValues}
            tickLabelProps={() => ({
              fill: "#333",
              fontSize: 10,
              textAnchor: "end"
            })}
            tickFormat={d => {
              //@ts-ignore
              return format(new Date(d), "dd MMM");
            }}
          />
        </Group>
        <Group left={margin.left + 16} top={16} key="control">
          <rect
            x={0}
            y={0}
            width={80}
            height={18}
            fill={"#4A5568"}
            rx={4}
            opacity={0.8}
            onClick={() => {
              _scaleType(s => (s === "log" ? "linear" : "log"));
            }}
          />
          <Text
            style={{ pointerEvents: "none" }}
            x={40}
            y={5}
            width={100}
            textAnchor="middle"
            fontSize="10px"
            verticalAnchor="start"
            fill="#fff"
          >
            {`toon ${scaleType === "log" ? "linear" : "log"}`}
          </Text>
        </Group>
        <Group key="legend" left={margin.left + 20} top={margin.top + 28}>
          <GraphLegend
            entries={[
              {
                fill: cTotal,
                label: "Opgenomen patiënten"
              },
              {
                fill: cDelta,
                label: "Toename"
              },
              {
                fill: cDeath,
                label: "Slachtoffers"
              },
              {
                fill: cIC,
                label: "IC opnames"
              },
              {
                fill: cInfected,
                label: "Positief geteste personen"
              }
            ]}
          />
        </Group>
      </svg>
    </div>
  );
}

// {({ path }) => (
//   <motion.path
//     d={path(data)}
//     fill={cTotal}
//     animate={{
//       d: path(data)
//     }}
//     transition={{ ease: "easeOut" }}
//   />
// )}

// {data.map((d, i) => {
//   const cx = x(d);
//   const cy = y(d);
//   const cy2 = y2(d);
//   const cy3 = y3(d);
//   return (
//     <g key={`line-point-${i}`}>
//       <GlyphDot
//         cx={cx}
//         cy={cy}
//         r={3}
//         fill={contrast}
//         stroke={cTotal}
//         strokeWidth={1}
//         onMouseOver={() => {
//           _index(i);
//         }}
//       />
//       <GlyphDot
//         cx={cx}
//         cy={cy2}
//         r={3}
//         fill={contrast}
//         stroke={cDelta}
//         strokeWidth={1}
//         onMouseOver={() => {
//           _index(i);
//         }}
//       />
//       <GlyphDot
//         cx={cx}
//         cy={cy3}
//         r={3}
//         fill={contrast}
//         stroke={cDeath}
//         strokeWidth={1}
//         onMouseOver={() => {
//           _index(i);
//         }}
//       />
//     </g>
//   );
// })}
